import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, X, Download, Send, Plus, Save, Eye, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, StatusBadge, Empty, chf } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const REPORT_STATI = ["entwurf", "bereit", "gesendet"];
const cls = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#A3E635]";
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const { t } = useI18n();
  const [reports, setReports] = useState(null);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [filter, setFilter] = useState("alle");
  const [edit, setEdit] = useState(null); // report being edited
  const [busyBulk, setBusyBulk] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creationNotice, setCreationNotice] = useState("");
  const createPending = useRef(false);
  const bulkPending = useRef(false);
  const createButton = useRef(null);
  const lastTrigger = useRef(null);

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => { setClients(r.data); setSel(r.data[0]?.id || ""); }); }, []);
  if (!reports) return <Spinner />;

  const sameMonth = (r) => r.client_id === sel && r.month === month;
  const existingDraft = reports.find((r) => sameMonth(r) && (r.status || "entwurf") === "entwurf") || reports.find(sameMonth);

  const createReport = async () => {
    if (!sel || !month || createPending.current || bulkPending.current) return;
    lastTrigger.current = createButton.current;
    if (existingDraft) { setEdit(existingDraft); return; }
    createPending.current = true;
    setCreationNotice("");
    setCreating(true);
    try {
      const { data } = await api.post("/reports", { client_id: sel, month });
      setReports((rs) => [data, ...rs.filter((r) => r.id !== data.id)]);
      setEdit(data);
      toast.success(t(data.reused ? "Vorhandener Report geöffnet" : "Monatsreport mit echten Daten erstellt"));
      try { await load(); }
      catch { setCreationNotice(t("Report erstellt. Die Liste konnte nicht neu geladen werden.")); }
    } catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); }
    finally { createPending.current = false; setCreating(false); }
  };
  const setStatus = async (rid, status) => {
    const { data } = await api.put(`/reports/${rid}`, { status });
    setReports((rs) => rs.map((r) => (r.id === rid ? data : r)));
    toast.success(status === "gesendet" ? t("An Kunde gesendet") : t("Status aktualisiert"));
  };
  const removeReport = async (r) => {
    if (!window.confirm(t("Report «{x}» wirklich löschen?").replace("{x}", `${r.company_name} · ${r.month_label || r.month}`))) return;
    try {
      await api.delete(`/reports/${r.id}`);
      setReports((rs) => rs.filter((x) => x.id !== r.id));
      toast.success(t("Report gelöscht"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Löschen fehlgeschlagen")); }
  };
  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/reports/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error(t("PDF konnte nicht geladen werden.")); }
  };

  const generateAll = async () => {
    if (bulkPending.current || createPending.current) return;
    bulkPending.current = true;
    setBusyBulk(true);
    try {
      const { data } = await api.post("/reports/generate-month", { month });
      const base = t("{n} Report(s) für {month} erstellt").replace("{n}", data.created).replace("{month}", data.month_label);
      toast.success(base + (data.skipped ? t(", {n} bereits vorhanden").replace("{n}", data.skipped) : ""));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Fehler bei der Sammelerstellung")); }
    finally { bulkPending.current = false; setBusyBulk(false); }
  };

  const shown = filter === "alle" ? reports : reports.filter((r) => r.status === filter);

  return (
    <div className="fade-in">
      <PageHeader title={t("Monatsreports")} subtitle={t("Monatliche Auswertung pro Kunde — echte Zahlen, editierbar, als PDF & im Cockpit.")} />

      <Card className="p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label htmlFor="report-client" className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Kunde")}</label>
          <select id="report-client" value={sel} disabled={creating || busyBulk} onChange={(e) => setSel(e.target.value)} data-testid="report-client" className={`mt-1 ${cls}`}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="report-month" className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Monat")}</label>
          <input id="report-month" type="month" value={month} disabled={creating || busyBulk} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" className={`mt-1 ${cls}`} />
        </div>
        <button ref={createButton} onClick={createReport} disabled={creating || busyBulk || !sel || !month} data-testid="report-create" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg flex items-center gap-2 min-h-[44px] disabled:opacity-60"><Plus className="w-4 h-4" /> {creating ? t("Erstelle …") : existingDraft ? t((existingDraft.status || "entwurf") === "entwurf" ? "Entwurf öffnen" : "Report öffnen") : t("Report erstellen")}</button>
        <button onClick={generateAll} disabled={busyBulk || creating} data-testid="report-generate-all" className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2.5 px-5 rounded-lg flex items-center gap-2 disabled:opacity-60"><FileText className="w-4 h-4" /> {busyBulk ? t("Erstelle …") : t("Für alle aktiven Kunden")}</button>
      </Card>

      {creationNotice && <p role="status" data-testid="report-creation-notice" className="text-sm text-amber-200 mb-4">{creationNotice}</p>}

      <div className="flex gap-2 mb-5 flex-wrap">
        {["alle", ...REPORT_STATI].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`report-filter-${s}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${filter === s ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>{t(s)}</button>
        ))}
      </div>

      {shown.length === 0 ? <Empty text={t("Keine Reports in dieser Ansicht.")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {shown.map((r) => (
            <Card key={r.id} className="p-5 flex flex-col" data-testid={`report-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold">{r.company_name}</span>
                <StatusBadge status={r.status || "entwurf"} />
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{r.month_label || r.month}</div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <Metric v={chf(r.visits).replace("CHF", "").trim()} l={t("Besuche")} />
                <Metric v={r.leads_count ?? 0} l={t("Neue Anfragen")} />
                <Metric v={r.completed_tasks ?? 0} l={t("Aufgaben erledigt")} />
                <Metric v={r.uptime_pct ? `${r.uptime_pct}%` : "—"} l={t("Verfügbarkeit")} />
              </div>
              <p className="text-sm text-gray-400 mt-3 line-clamp-3 flex-1">{r.summary}</p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button onClick={(e) => { lastTrigger.current = e.currentTarget; setEdit(r); }} data-testid={`report-edit-${r.id}`} className="inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2 rounded-lg"><Eye className="w-4 h-4" /> {t("Bearbeiten")}</button>
                <button onClick={() => downloadPdf(r)} data-testid={`report-pdf-${r.id}`} className="inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2 rounded-lg"><Download className="w-4 h-4" /> PDF</button>
              </div>
              <select value={r.status || "entwurf"} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`report-status-${r.id}`} className={`mt-2 ${cls} py-1.5 capitalize`}>
                {REPORT_STATI.map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select>
              <button onClick={() => removeReport(r)} data-testid={`report-delete-${r.id}`} className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /> {t("Löschen")}</button>
            </Card>
          ))}
        </div>
      )}

      {edit && <ReportEditor key={edit.id} report={edit} returnFocusRef={lastTrigger} onClose={() => setEdit(null)} onSaved={(d) => { setReports((rs) => rs.map((r) => (r.id === d.id ? d : r))); setEdit(d); }} onPdf={downloadPdf} />}
    </div>
  );
}

const Metric = ({ v, l }) => (
  <div><div className="text-2xl font-black tracking-tighter">{v}</div><div className="text-[10px] uppercase tracking-wider text-gray-500">{l}</div></div>
);

function ReportEditor({ report, onClose, onSaved, onPdf, returnFocusRef }) {
  const { t } = useI18n();
  const [f, setF] = useState({
    summary: report.summary || "", recommendation: report.recommendation || "",
    open_points: report.open_points || "", highlights: (report.highlights || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const summaryInput = useRef(null);
  savingRef.current = saving;
  const save = async (extra = {}) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.put(`/reports/${report.id}`, {
        summary: f.summary, recommendation: f.recommendation, open_points: f.open_points,
        highlights: f.highlights.split("\n").map((x) => x.trim()).filter(Boolean), ...extra,
      });
      toast.success(extra.status === "gesendet" ? t("An Kunde gesendet") : t("Gespeichert"));
      onSaved(data);
      if (extra.status === "gesendet") onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); }
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !savingRef.current) onClose(); }}>
      <DialogContent data-testid="report-editor" closeLabel={t("Schliessen")} closeDisabled={saving}
        className="w-[calc(100vw_-_2rem)] max-w-2xl max-h-[90dvh] overflow-y-auto min-w-0 bg-[#121217] border-white/10 text-white rounded-2xl p-4 sm:p-6"
        onOpenAutoFocus={(e) => { e.preventDefault(); summaryInput.current?.focus(); }}
        onCloseAutoFocus={(e) => { e.preventDefault(); returnFocusRef?.current?.focus(); }}
        onEscapeKeyDown={(e) => { if (savingRef.current) e.preventDefault(); }}
        onInteractOutside={(e) => { if (savingRef.current) e.preventDefault(); }}>
        <div className="pr-8">
          <DialogTitle className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-[#A3E635]" /> {t("Report bearbeiten")}</DialogTitle>
          <DialogDescription className="text-sm text-gray-400 mt-1">{report.company_name} · {report.month_label || report.month}</DialogDescription>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 mt-3">
          <Metric v={chf(report.visits).replace("CHF", "").trim()} l={t("Besuche")} />
          <Metric v={report.leads_count ?? 0} l={t("Anfragen")} />
          <Metric v={report.completed_tasks ?? 0} l={t("Erledigt")} />
          <Metric v={report.uptime_pct ? `${report.uptime_pct}%` : "—"} l={t("Uptime")} />
        </div>

        <fieldset disabled={saving} className="space-y-3">
          <Lbl t={t("Zusammenfassung")}><textarea ref={summaryInput} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="re-summary" rows={4} className={cls} /></Lbl>
          <Lbl t={t("Highlights (eine pro Zeile)")}><textarea value={f.highlights} onChange={(e) => setF({ ...f, highlights: e.target.value })} data-testid="re-highlights" rows={4} className={cls} /></Lbl>
          <Lbl t={t("Offene Punkte")}><textarea value={f.open_points} onChange={(e) => setF({ ...f, open_points: e.target.value })} data-testid="re-openpoints" rows={2} className={cls} /></Lbl>
          <Lbl t={t("Empfehlung")}><textarea value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="re-recommendation" rows={2} className={cls} /></Lbl>
        </fieldset>

        <div className="flex flex-wrap justify-between gap-3 mt-6">
          <button onClick={() => onPdf(report)} disabled={saving} className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2 px-4 rounded-lg disabled:opacity-60"><Download className="w-4 h-4" /> {t("PDF ansehen")}</button>
          <div className="flex gap-3">
            <button onClick={() => save()} disabled={saving} data-testid="re-save" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2 px-4 rounded-lg disabled:opacity-60"><Save className="w-4 h-4" /> {t("Speichern")}</button>
            <button onClick={() => save({ status: "gesendet" })} disabled={saving} data-testid="re-send" className="inline-flex items-center gap-2 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg disabled:opacity-60"><Send className="w-4 h-4" /> {t("Speichern & Senden")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Lbl = ({ t, children }) => (
  <div><label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label><div className="mt-1">{children}</div></div>
);
